// src/services/callGraphProfileService.js
import axios from 'axios'

/**
 * Call Graph Profiles (Management CRUD) — V1
 *
 * V1 SUPPORT:
 *  - USER
 *  - NUMBER
 *
 * Routes used:
 *  GET    /call-graph-profiles
 *  GET    /call-graph-profiles/{id}
 *  POST   /call-graph-profiles
 *  PUT    /call-graph-profiles/{id}
 *  DELETE /call-graph-profiles/{id}
 *  POST   /call-graph-profiles/{id}/public-link
 *  DELETE /call-graph-profiles/{id}/public-link
 *
 * Public view route (no auth) is separate:
 *  GET /public/call-graphs/{profile_id}/{token}
 */

const V1_GRAPH_TYPES = ['USER', 'NUMBER']
const TIMEFRAMES = ['LAST_15_MIN', 'LAST_1_HOUR', 'LAST_24_HOURS', 'LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM']
const INTERVALS = ['1_MIN', '5_MIN', '15_MIN', '1_HOUR', '1_DAY']

function normCustomerId(input) {
  return String(
    input?.customer_id ??
      input?.CustomerID ??
      input?.customerId ??
      input?.customerID ??
      input?.accountcode ??
      input?.accountCode ??
      ''
  ).trim()
}

function ensureProfileId(id) {
  const v = Number(id)
  if (!Number.isFinite(v) || v <= 0) throw new Error(`Invalid profile id: "${id}"`)
  return v
}

function ensureEnum(val, allowed, label) {
  if (!allowed.includes(val)) {
    throw new Error(`Invalid ${label}: "${val}". Allowed: ${allowed.join(', ')}`)
  }
  return val
}

function normalizeEntities(graphType, entities) {
  const list = Array.isArray(entities) ? entities : []

  return list.map((e, idx) => {
    const entity_type = String(e?.entity_type ?? e?.entityType ?? graphType ?? '').trim()
    const entity_ref = String(e?.entity_ref ?? e?.entityRef ?? e?.ref ?? '').trim()

    if (!entity_type) throw new Error(`entities[${idx}].entity_type is required`)
    if (!entity_ref) throw new Error(`entities[${idx}].entity_ref is required`)

    // V1 hard-gate: only USER/NUMBER entity types allowed
    ensureEnum(entity_type, V1_GRAPH_TYPES, `entities[${idx}].entity_type`)

    const label_override = e?.label_override ?? e?.labelOverride ?? null
    const display_order = e?.display_order ?? e?.displayOrder ?? (Number.isFinite(idx) ? idx : 0)
    const is_enabled = e?.is_enabled ?? e?.isEnabled ?? true

    return {
      entity_type,
      entity_ref,
      label_override: label_override ? String(label_override).slice(0, 80) : null,
      display_order: Number.isFinite(Number(display_order)) ? Number(display_order) : 0,
      is_enabled: !!is_enabled
    }
  })
}

/**
 * Persist UI fields (metrics/chartType/barLayout) into config_json
 * because backend stores these under config_json (and UI reads them back).
 */
function normalizeConfigJson(input) {
  const raw = input?.config_json ?? input?.configJson ?? {}

  let cfg = {}
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) cfg = { ...raw }

  if (Array.isArray(input?.metrics)) cfg.metrics = [...input.metrics]

  if (typeof input?.chartType === 'string' && input.chartType.trim()) {
    cfg.chartType = input.chartType.trim()
  }

  if (typeof input?.barLayout === 'string' && input.barLayout.trim()) {
    cfg.barLayout = input.barLayout.trim()
  }

  return cfg
}

function normalizeProfilePayload(input, customer_id) {
  const name = String(input?.name ?? '').trim()
  if (!name) throw new Error('Profile name is required')

  const graph_type = ensureEnum(
    String(input?.graph_type ?? input?.graphType ?? '').trim(),
    V1_GRAPH_TYPES,
    'graph_type'
  )

  const default_timeframe = String(input?.default_timeframe ?? input?.defaultTimeframe ?? 'LAST_24_HOURS').trim()
  ensureEnum(default_timeframe, TIMEFRAMES, 'default_timeframe')

  const default_interval = String(input?.default_interval ?? input?.defaultInterval ?? '15_MIN').trim()
  ensureEnum(default_interval, INTERVALS, 'default_interval')

  const timezone = String(input?.timezone ?? 'America/Phoenix').trim() || 'America/Phoenix'
  const description = input?.description == null ? null : String(input.description).slice(0, 255)

  const config_json = normalizeConfigJson(input)
  const is_enabled = input?.is_enabled ?? input?.isEnabled ?? true

  // Entities: allow UI to pass array of refs; coerce into proper objects.
  const rawEntities =
    input?.entities ??
    input?.entityRefs?.map((ref, i) => ({
      entity_type: graph_type,
      entity_ref: ref,
      display_order: i
    })) ??
    []

  const entities = normalizeEntities(graph_type, rawEntities)

  // Enforce entity_type matches graph_type (same invariant as backend)
  for (const e of entities) {
    if (e.entity_type !== graph_type) {
      throw new Error(`Entity type must match graph_type. Got "${e.entity_type}" expected "${graph_type}"`)
    }
  }

  return {
    customer_id,
    name,
    description,
    graph_type,
    default_timeframe,
    default_interval,
    timezone,
    config_json,
    is_enabled: !!is_enabled,
    entities
  }
}

export default {
  async list(input = {}) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')

    const rawGraphType = input?.graph_type ?? input?.graphType ?? undefined
    const graph_type = rawGraphType == null || rawGraphType === ''
      ? undefined
      : ensureEnum(String(rawGraphType).trim(), V1_GRAPH_TYPES, 'graph_type')

    const params = {
      customer_id,
      q: input?.q ?? '',
      graph_type,
      include_entities: input?.include_entities ?? input?.includeEntities ?? true
    }

    const res = await axios.get('/call-graph-profiles', { params })
    return res.data
  },

  async get(id, input = {}) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')
    const pid = ensureProfileId(id)

    const res = await axios.get(`/call-graph-profiles/${pid}`, {
      params: { customer_id }
    })
    return res.data
  },

  async create(input) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')

    const payload = normalizeProfilePayload(input, customer_id)
    const res = await axios.post('/call-graph-profiles', payload)
    return res.data
  },

  async update(id, input) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')
    const pid = ensureProfileId(id)

    const payload = normalizeProfilePayload(input, customer_id)
    const res = await axios.put(`/call-graph-profiles/${pid}`, payload)
    return res.data
  },

  async remove(id, input = {}) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')
    const pid = ensureProfileId(id)

    const res = await axios.delete(`/call-graph-profiles/${pid}`, {
      params: { customer_id }
    })
    return res.data
  },

  async createOrRotatePublicLink(id, input = {}) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')
    const pid = ensureProfileId(id)

    const res = await axios.post(`/call-graph-profiles/${pid}/public-link`, {
      customer_id
    })
    return res.data
  },

  async disablePublicLink(id, input = {}) {
    const customer_id = normCustomerId(input)
    if (!customer_id) throw new Error('Missing customer_id')
    const pid = ensureProfileId(id)

    const res = await axios.delete(`/call-graph-profiles/${pid}/public-link`, {
      params: { customer_id }
    })
    return res.data
  },

  /**
   * Public (no auth) fetch by URL parts.
   */
  async getPublic(profile_id, token) {
    const pid = ensureProfileId(profile_id)
    const t = String(token ?? '').trim()
    if (!t) throw new Error('Missing token')

    const res = await axios.get(`/public/call-graphs/${pid}/${encodeURIComponent(t)}`)
    return res.data
  }
}
